import * as React from 'react';
import { FormEvent } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Spinner from 'reactstrap/lib/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLongArrowAltRight } from '@fortawesome/free-solid-svg-icons/faLongArrowAltRight';
import { connect, MapStateToPropsParam } from 'react-redux';
import { ApplicationState } from '../store';
import selectors from '../store/selectors';
import { AuthenticationMethod, ResponseError, ResponseErrorCode } from '../apis';
import { verifyAction } from '../store/actions';
import { bindActionCreators, Dispatch } from 'redux';
import MaskedInput from 'react-text-mask';
import FormFeedback from './FormFeedback';
import FormGroup from 'reactstrap/lib/FormGroup';
import Form from 'reactstrap/lib/Form';
import MESSAGE, { RESEND_COOLDOWN_SECONDS } from '../message';

interface StateProps {
    isRequesting: boolean;
    error: ResponseError | null;
    codeLength: number;
}

// Code length is always 4 (legacy) or 6 (Telnyx); fall back to 4 for any unexpected value (0 / NaN).
const MIN_CODE_LENGTH = 4;
const clampCodeLength = (length: number): number =>
    Number.isFinite(length) && length >= MIN_CODE_LENGTH ? Math.floor(length) : MIN_CODE_LENGTH;

// Builds a react-text-mask mask of `length` digit cells separated by spaces, e.g. for 4: X X X X
const buildCodeMask = (length: number): (RegExp | string)[] =>
    Array.from({ length: clampCodeLength(length) }).flatMap((_, index) => (index === 0 ? [/[0-9]/] : [' ', /[0-9]/]));

const buildCodePlaceholder = (length: number): string =>
    Array.from({ length: clampCodeLength(length) }, () => 'X').join(' ');

type OwnProps = {
    show: boolean;
    authenticationMethod: AuthenticationMethod;
    value: string;
    isResending: boolean;
    resendError?: ResponseError | null;
    onClose: () => void;
    onResend: () => void;
    onSuccess?: () => void;
};

interface DispatchProps {
    verify: typeof verifyAction;
}

type Props = OwnProps & StateProps & DispatchProps;

type State = {
    code: string;
    resendCooldown: number;
};

class VerificationCodeModal extends React.Component<Props, State> {
    public state: State = {
        code: '',
        resendCooldown: 0,
    };

    private cooldownInterval: ReturnType<typeof setInterval> | null = null;

    componentDidMount() {
        if (this.props.show) {
            this.startResendCooldown();
        }
    }

    componentDidUpdate(prevProps: Props) {
        // A code was just sent when the modal opens.
        if (this.props.show && !prevProps.show) {
            this.startResendCooldown();
        }
    }

    componentWillUnmount() {
        this.clearCooldownInterval();
    }

    startResendCooldown = () => {
        this.clearCooldownInterval();
        this.setState({ resendCooldown: RESEND_COOLDOWN_SECONDS });
        this.cooldownInterval = setInterval(() => {
            this.setState((state: State) => {
                const resendCooldown = state.resendCooldown - 1;
                if (resendCooldown <= 0) {
                    this.clearCooldownInterval();
                }
                return { resendCooldown: Math.max(resendCooldown, 0) };
            });
        }, 1000);
    };

    clearCooldownInterval = () => {
        if (this.cooldownInterval) {
            clearInterval(this.cooldownInterval);
            this.cooldownInterval = null;
        }
    };

    onResend = () => {
        this.startResendCooldown();
        this.props.onResend();
    };

    onToggle = () => {
        this.props.onClose();
    };

    onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        try {
            await this.props.verify(this.state.code);
            if (this.props.onSuccess) {
                this.props.onSuccess();
            }
        } catch {
            // Error
        }
    };

    render() {
        const { show, isRequesting, error, isResending, resendError, codeLength } = this.props;
        const { resendCooldown } = this.state;

        return (
            <Modal isOpen={show} toggle={this.onToggle} centered>
                <Form onSubmit={this.onSubmit}>
                    <ModalHeader toggle={this.onToggle}>Verification</ModalHeader>
                    <ModalBody>
                        <p>
                            Enter the verification code sent to <strong>{this.props.value}</strong>.
                        </p>
                        <FormGroup className="mb-0">
                            {this.props.show ? (
                                <MaskedInput
                                    mask={buildCodeMask(codeLength)}
                                    type="text"
                                    placeholder={buildCodePlaceholder(codeLength)}
                                    className={[
                                        'form-control form-control-lg text-center',
                                        error ? 'is-invalid' : '',
                                    ].join(' ')}
                                    onChange={(e) => this.setState({ code: e.currentTarget.value })}
                                    ref={(input) => {
                                        setTimeout(() => {
                                            if (input && input.inputElement) {
                                                input.inputElement.focus();
                                            }
                                        }, 500);
                                    }}
                                />
                            ) : null}
                            <FormFeedback
                                error={error}
                                options={{
                                    // The backend sends specific messages (wrong code / already used /
                                    // expired); only fall back to the generic text when there is none.
                                    transform: (responseError: ResponseError) => {
                                        if (
                                            responseError.code === ResponseErrorCode.InvalidField &&
                                            !responseError.message
                                        ) {
                                            return `${MESSAGE.ERROR_INCORRECT_CODE} ${MESSAGE.CHECK_AND_TRY_AGAIN}`;
                                        }
                                    },
                                }}
                            />
                            {resendError ? (
                                <div className="text-danger small d-block mt-1">
                                    {resendError.code === ResponseErrorCode.TooManyRequests
                                        ? MESSAGE.ERROR_TOO_MANY_CODE_REQUESTS_MODAL
                                        : selectors.global.getErrorMessage(resendError)}
                                </div>
                            ) : null}
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter className="d-flex justify-content-between">
                        <Button color="light" onClick={this.onResend} disabled={isResending || resendCooldown > 0}>
                            {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend'}
                        </Button>
                        <Button type={'submit'} disabled={isResending || this.props.isRequesting} color="primary">
                            Verify{' '}
                            {isRequesting || isResending ? (
                                <Spinner type="grow" color="white" size={'sm'} />
                            ) : (
                                <FontAwesomeIcon icon={faLongArrowAltRight} fixedWidth />
                            )}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        );
    }
}

const mapStateToProps: MapStateToPropsParam<StateProps, OwnProps, ApplicationState> = (state) => ({
    error: selectors.global.getResponseError(state.global.actions, 'verify'),
    isRequesting: selectors.global.isActionRequesting(state.global.actions, 'verify'),
    codeLength: state.authentication.codeLength,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {
            verify: verifyAction,
        },
        dispatch,
    );

export default connect(mapStateToProps, mapDispatchToProps)(VerificationCodeModal);
